import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import {
  MessageCircle,
  ArrowLeft,
  Send,
  Users,
  Sparkles,
  Plus,
  Wifi,
  WifiOff,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Convert HTTP URL to WebSocket URL
const getWebSocketUrl = () => {
  const url = new URL(BACKEND_URL);
  const protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${url.host}`;
};

const ChatPage = ({ user }) => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [suggestedAnswers, setSuggestedAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Get session token from localStorage (cookie is HTTP-only, can't be read by JS)
  const getSessionToken = useCallback(() => {
    try {
      return localStorage.getItem("lh_session_token");
    } catch (e) {
      return null;
    }
  }, []);

  // Connect to WebSocket
  const connectWebSocket = useCallback((conversationId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }

    const token = getSessionToken();
    if (!token) {
      console.error("No session token found");
      return;
    }

    const wsUrl = `${getWebSocketUrl()}/ws/chat/${conversationId}?token=${token}`;
    console.log("Connecting to WebSocket:", wsUrl);

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log("WebSocket connected");
      setIsConnected(true);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("WebSocket message:", data);

        if (data.type === "new_message") {
          setMessages((prev) => {
            // Avoid duplicates
            const exists = prev.some(
              (m) => m.message_id === data.message.message_id
            );
            if (exists) return prev;
            return [...prev, data.message];
          });
        } else if (data.type === "typing") {
          if (data.user_id !== user?.user_id) {
            setOtherUserTyping(data.is_typing);
            // Auto-clear typing indicator after 3 seconds
            if (data.is_typing) {
              setTimeout(() => setOtherUserTyping(false), 3000);
            }
          }
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    ws.onclose = (event) => {
      console.log("WebSocket disconnected:", event.code, event.reason);
      setIsConnected(false);

      // Attempt to reconnect after 3 seconds if not intentionally closed
      if (event.code !== 1000 && selectedConversation) {
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log("Attempting to reconnect...");
          connectWebSocket(conversationId);
        }, 3000);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      setIsConnected(false);
    };

    wsRef.current = ws;
  }, [getSessionToken, user?.user_id, selectedConversation]);

  // Disconnect WebSocket
  const disconnectWebSocket = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, "User navigating away");
      wsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    fetchConversations();
    if (user?.role === "local_homey" && user?.local_homey_country) {
      fetchSuggestedAnswers();
    }

    return () => {
      disconnectWebSocket();
    };
  }, [user, disconnectWebSocket]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages();
      connectWebSocket(selectedConversation.conversation_id);
    }

    return () => {
      if (selectedConversation) {
        disconnectWebSocket();
      }
    };
  }, [selectedConversation, connectWebSocket, disconnectWebSocket]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API}/conversations`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
        if (data.length > 0 && !selectedConversation) {
          setSelectedConversation(data[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!selectedConversation) return;
    try {
      const response = await fetch(
        `${API}/conversations/${selectedConversation.conversation_id}/messages`,
        { credentials: "include" }
      );
      if (response.ok) {
        setMessages(await response.json());
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const fetchSuggestedAnswers = async () => {
    try {
      const response = await fetch(`${API}/suggested-answers/${user.local_homey_country}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setSuggestedAnswers(data.answers || []);
      }
    } catch (error) {
      console.error("Error fetching suggested answers:", error);
    }
  };

  const handleRequestMatch = async () => {
    if (!user?.target_country) {
      toast.error("Please select a target country first");
      return;
    }

    try {
      const response = await fetch(`${API}/match/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ country: user.target_country }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Matched with ${data.homey.name}!`);
        fetchConversations();
      } else {
        const error = await response.json();
        toast.error(error.detail || "No Local Homey available");
      }
    } catch (error) {
      toast.error("Error requesting match");
    }
  };

  const handleSendMessage = async (content = newMessage, isSuggested = false) => {
    if (!content.trim() || !selectedConversation) return;

    // Try WebSocket first for real-time delivery
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: "message",
          content: content.trim(),
          is_suggested: isSuggested,
        })
      );
      setNewMessage("");
      return;
    }

    // Fallback to HTTP if WebSocket not connected
    setSending(true);
    try {
      const response = await fetch(
        `${API}/conversations/${selectedConversation.conversation_id}/messages`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ content: content.trim(), is_suggested: isSuggested }),
        }
      );

      if (response.ok) {
        const message = await response.json();
        setMessages((prev) => [...prev, message]);
        setNewMessage("");
      }
    } catch (error) {
      toast.error("Error sending message");
    } finally {
      setSending(false);
    }
  };

  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: "typing",
          is_typing: true,
        })
      );

      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Send stop typing after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({
              type: "typing",
              is_typing: false,
            })
          );
        }
      }, 2000);
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";
  };

  const isOwnMessage = (senderId) => senderId === user?.user_id;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0] px-4 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-[#4A5568]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#4A6C6F] rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#1A202C]">
                {user?.role === "local_homey" ? "Help Newbies" : "Chat with Local Homeys"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Connection Status */}
            <div
              data-testid="connection-status"
              className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                isConnected
                  ? "bg-[#48BB78]/20 text-[#48BB78]"
                  : "bg-[#ED8936]/20 text-[#ED8936]"
              }`}
            >
              {isConnected ? (
                <>
                  <Wifi className="w-4 h-4" />
                  <span className="hidden sm:inline">Live</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4" />
                  <span className="hidden sm:inline">Connecting...</span>
                </>
              )}
            </div>
            {user?.role === "newbie" && (
              <Button
                data-testid="find-homey-btn"
                onClick={handleRequestMatch}
                className="bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Find a Local Homey
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#4A6C6F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : conversations.length === 0 ? (
          <Card className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-[#4A6C6F]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-[#4A6C6F]" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#1A202C] mb-2">
                {user?.role === "local_homey" ? "No conversations yet" : "Connect with a Local Homey"}
              </h3>
              <p className="text-[#4A5568] mb-6 max-w-md mx-auto">
                {user?.role === "local_homey"
                  ? "Wait for newbies to reach out, or share your profile to get matched!"
                  : "Get matched with someone living in your destination country who can answer all your questions."}
              </p>
              {user?.role === "newbie" && (
                <Button
                  data-testid="get-matched-btn"
                  onClick={handleRequestMatch}
                  className="bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full px-8"
                >
                  Get Matched Now
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6 h-[calc(100vh-200px)]">
            {/* Conversations List */}
            <Card className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-[#1A202C]">Conversations</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[calc(100vh-320px)]">
                  <div className="space-y-1 p-4">
                    {conversations.map((conv) => (
                      <button
                        key={conv.conversation_id}
                        data-testid={`conversation-${conv.conversation_id}`}
                        onClick={() => setSelectedConversation(conv)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${
                          selectedConversation?.conversation_id === conv.conversation_id
                            ? "bg-[#4A6C6F]/15"
                            : "hover:bg-[#F5F5F0]"
                        }`}
                      >
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={conv.other_user?.picture} />
                          <AvatarFallback className="bg-[#4A6C6F] text-white">
                            {getInitials(conv.other_user?.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#1A202C] truncate">
                            {conv.other_user?.name || "Unknown"}
                          </p>
                          <p className="text-sm text-[#718096] capitalize">
                            {conv.country === "ireland" && "🇮🇪"}
                            {conv.country === "uk" && "🇬🇧"}
                            {conv.country === "canada" && "🇨🇦"} {conv.country}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Chat Area */}
            <Card className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl lg:col-span-2 flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-[#E2E8F0]">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={selectedConversation.other_user?.picture} />
                        <AvatarFallback className="bg-[#4A6C6F] text-white">
                          {getInitials(selectedConversation.other_user?.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-[#1A202C]">
                          {selectedConversation.other_user?.name}
                        </p>
                        <p className="text-sm text-[#718096]">
                          {user?.role === "local_homey" ? "Newbie" : "Local Homey"} •{" "}
                          {selectedConversation.country}
                          {otherUserTyping && (
                            <span className="ml-2 text-[#4A6C6F] animate-pulse">
                              is typing...
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.map((msg) => (
                        <div
                          key={msg.message_id}
                          data-testid={`message-${msg.message_id}`}
                          className={`flex ${isOwnMessage(msg.sender_id) ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[70%] px-4 py-3 ${
                              isOwnMessage(msg.sender_id)
                                ? "bg-[#4A6C6F] text-white rounded-2xl rounded-br-sm"
                                : "bg-[#F5F5F0] text-[#1A202C] rounded-2xl rounded-bl-sm"
                            }`}
                          >
                            <p>{msg.content}</p>
                            {msg.is_suggested && (
                              <div className="flex items-center gap-1 mt-2 text-xs opacity-70">
                                <Sparkles className="w-3 h-3" />
                                <span>Suggested answer</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Suggested Answers (for Local Homeys) */}
                  {user?.role === "local_homey" && suggestedAnswers.length > 0 && (
                    <div className="p-4 border-t border-[#E2E8F0] bg-[#F5F5F0]">
                      <p className="text-xs text-[#718096] mb-2 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Quick replies
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {suggestedAnswers.slice(0, 3).map((answer, index) => (
                          <button
                            key={index}
                            data-testid={`suggested-${index}`}
                            onClick={() => handleSendMessage(answer, true)}
                            className="text-sm px-3 py-2 bg-white border border-[#E2E8F0] rounded-full hover:border-[#4A6C6F] hover:bg-[#4A6C6F]/5 transition-colors text-left truncate max-w-[200px]"
                          >
                            {answer.slice(0, 50)}...
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Input */}
                  <div className="p-4 border-t border-[#E2E8F0]">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleSendMessage();
                      }}
                      className="flex gap-3"
                    >
                      <Input
                        data-testid="message-input"
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => {
                          setNewMessage(e.target.value);
                          handleTyping();
                        }}
                        className="flex-1 rounded-full border-[#E2E8F0]"
                      />
                      <Button
                        data-testid="send-btn"
                        type="submit"
                        disabled={sending || !newMessage.trim()}
                        className="bg-[#4A6C6F] hover:bg-[#3A5658] text-white rounded-full px-6"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-[#718096]">
                  <p>Select a conversation to start chatting</p>
                </div>
              )}
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default ChatPage;
